import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Skeleton, Card, CardContent, CardMedia, Typography, useTheme, alpha, Theme } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import { baseUrl, Endpoints } from '../api';
import { useAppSelector } from '../redux/hooks';
import { selectUserId } from '../redux/slices/user';
import { postData } from '../redux/thunks/itemsApi';
import AppText from './atoms/AppText';
import './Appcard.css';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { CurrencyCode } from '../schemes/items';
import { formatPrice } from '../utils/money';

interface IProps {
  type: 'top' | 'normal' | 'preview';
  details:
  | {
    title: string;
    image: string;
    originalImage: string;
    itemStatus: string;
    price: number;
    currency: string;
    city: string;
    createdDate: string;
    like: boolean | number;
    id: number;
    userId: number;
    convertedCurrency?: string;
    convertedPrice?: number;
  }
  | any;
  marginHorizontal?: number;
  /** Lets a parent take a visual-only copy of the card out of the tab order (e.g. the duplicated half of the Top Products marquee). */
  tabIndex?: number;
}

/** Plain left-clicks navigate in-app; modified/middle clicks keep the browser's default (open in new tab, etc.). */
const isPlainLeftClick = (e: React.MouseEvent) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

function AppCard({ type, details, tabIndex }: IProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigation = useNavigate();
  const userId = useAppSelector(selectUserId);
  const { title, image, itemStatus, price, currency, city, createdDate, like, id: itemId, convertedCurrency, convertedPrice } = details;

  const [isLiked, setIsLiked] = useState(() => {
    return typeof like === 'number' ? like !== 0 : like;
  });
  const [isFavIconDisabled, setIsFavIconDisabled] = useState(userId === details.userId);
  const [hasImageError, setHasImageError] = useState(false);

  const getFavoriteIconColor = useMemo(() => {
    if (isLiked) {
      return theme.palette.error.main; // Usually a shade of red
    }
    // When not liked, use a more visible color
    return theme.palette.mode === 'light'
      ? theme.palette.grey[400] // Lighter grey for light mode
      : theme.palette.grey[600]; // Darker grey for dark mode
  }, [isLiked, theme]);

  const imgUrl = useMemo(() => {
    switch (type) {
      case 'preview':
        return image;
      case 'top':
        return Endpoints.photoOriginal + image;
      default:
        return Endpoints.photoThumb + image;
    }
  }, [type, image]);

  // A new image (e.g. recycled card) gets a fresh chance to load
  useEffect(() => {
    setHasImageError(false);
  }, [imgUrl]);

  // usage
  const priceTxt = formatPrice(convertedPrice ?? price, (convertedCurrency ?? currency) as CurrencyCode);

  const likeApiPost = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!userId) {
        alert('Please login');
        return;
      }

      setIsFavIconDisabled(true);
      try {
        const response = await postData(`${baseUrl}/Items/SetItemLike`, { itemId, userId });
        if (response) {
          setIsLiked((current: any) => !current);
        }
      } catch (error) {
      } finally {
        setIsFavIconDisabled(false);
      }
    },
    [userId, itemId]
  );

  const navigateToItemDetails = useCallback(() => {
    navigation(`/details/${itemId}`);
  }, [navigation, itemId]);

  if (type === 'top') {
    const metaText = [city, createdDate].filter((part) => typeof part === 'string' && part.trim()).join(' • ');
    const showImage = !!image && !hasImageError;
    const favoriteLabel = isLiked ? t('web.favorite-remove', 'Remove from favorites') : t('web.favorite-add', 'Add to favorites');

    return (
      <Box className="top-card" sx={topCardStyles.card}>
        <Box sx={topCardStyles.media}>
          {showImage ? (
            // Not lazy: the row slides itself, and browsers only re-check lazy images when the page repaints,
            // so cards that slide in would stay blank until the visitor scrolls. The original row loaded them all up front too.
            <Box component="img" src={imgUrl} alt={title} loading="eager" decoding="async" draggable={false} onError={() => setHasImageError(true)} sx={topCardStyles.image} />
          ) : (
            <Box sx={topCardStyles.imageFallback} data-testid="top-card-image-fallback">
              <ImageNotSupportedOutlinedIcon fontSize="large" />
            </Box>
          )}
          <Box component="span" aria-hidden="true" sx={topCardStyles.badge}>
            {t('main_screen_page.topBadge', 'TOP')}
          </Box>
        </Box>

        <Box sx={topCardStyles.body}>
          <Typography
            component="a"
            href={`/details/${itemId}`}
            tabIndex={tabIndex}
            title={title}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              if (!isPlainLeftClick(e)) return;
              e.preventDefault();
              navigateToItemDetails();
            }}
            sx={topCardStyles.title}
          >
            {title}
          </Typography>

          <Typography sx={topCardStyles.price}>{priceTxt}</Typography>

          {metaText ? <Typography sx={topCardStyles.meta}>{metaText}</Typography> : null}

          <Box sx={topCardStyles.footer}>
            <Typography sx={topCardStyles.status}>{itemStatus}</Typography>
            <IconButton aria-label={favoriteLabel} size="small" tabIndex={tabIndex} sx={topCardStyles.favoriteButton} onClick={likeApiPost} disabled={isFavIconDisabled}>
              {isLiked ? <FavoriteIcon sx={topCardStyles.favoriteIcon} /> : <FavoriteBorderIcon sx={topCardStyles.favoriteIcon} />}
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <a
      href={`/details/${itemId}`}
      style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flex: 1, minHeight: 0, width: '100%' }}
      onClick={(e) => {
        e.preventDefault();
        navigation(`/details/${itemId}`);
      }}
    >
      <Card
        className="app-card"
        sx={{
          ...styles.cardStyle,
          flex: 1,
          width: '100%',
          backgroundColor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
          cursor: 'pointer',
          '&:hover': {
            boxShadow: theme.shadows[2],
            transform: 'translateY(-1px)',
            transition: 'all 0.2s ease-in-out'
          }
        }}
      >
        {!imgUrl ? <Skeleton variant="rounded" sx={styles.skeletonStyle} animation="wave" /> : <CardMedia component="img" alt={title} image={imgUrl} sx={styles.imageStyle} loading="lazy" />}
        <CardContent sx={styles.contentStyle}>
          <Box sx={styles.titleBlockStyle}>
            <AppText textSize={12} style={styles.titleStyle} onClick={navigateToItemDetails}>
              {title}
            </AppText>
          </Box>

          <Box sx={styles.metaBlockStyle}>
            <AppText textSize={13} style={styles.priceStyle}>
              {priceTxt}
            </AppText>

            <AppText textSize={11} style={styles.addressStyle}>
              {[city, createdDate].filter((part) => typeof part === 'string' && part.trim()).join(', ')}
            </AppText>
          </Box>

          <Box sx={styles.itemStatusStyle}>
            <AppText textSize={11} style={styles.statusTextStyle}>
              {itemStatus}
            </AppText>

            <IconButton
              aria-label={isLiked ? t('web.favorite-remove', 'Remove from favorites') : t('web.favorite-add', 'Add to favorites')}
              size="small"
              sx={styles.favoriteButton}
              onClick={likeApiPost}
              disabled={isFavIconDisabled}
            >
              {type !== 'preview' &&
                (isLiked ? (
                  <FavoriteIcon fontSize="small" sx={{ color: getFavoriteIconColor }} />
                ) : (
                  <FavoriteBorderIcon fontSize="small" sx={{ color: getFavoriteIconColor }} />
                ))}
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    </a>
  );
}

export default React.memo(AppCard);

/** Loading placeholder with the exact footprint of a `type="top"` card. */
export const TopProductCardSkeleton = () => (
  <Box sx={topCardStyles.card} aria-hidden="true">
    <Skeleton variant="rounded" animation="wave" sx={{ ...topCardStyles.media, height: 'auto', transform: 'none' }} />
    <Box sx={topCardStyles.body}>
      <Skeleton variant="text" animation="wave" width="95%" />
      <Skeleton variant="text" animation="wave" width="70%" />
      <Skeleton variant="text" animation="wave" width="55%" sx={{ mt: 0.5 }} />
      <Skeleton variant="text" animation="wave" width="80%" />
      <Box sx={topCardStyles.footer}>
        <Skeleton variant="text" animation="wave" width="35%" />
        <Skeleton variant="circular" animation="wave" width={24} height={24} />
      </Box>
    </Box>
  </Box>
);

// Muted but AA-readable on the tinted card: palette.text.secondary is too faint in light mode (#9A9FA5) and identical to
// the title in dark mode (#FCFCFC), so take the nearest palette greys instead.
const metaColor = (theme: Theme) => (theme.palette.mode === 'light' ? theme.palette.grey[700] : theme.palette.secondary.main);

/**
 * Top Products card — mirrors the mobile app's card (teal-outlined tinted surface, inset rounded photo
 * with a TOP badge, 2-line title, teal price, "city • date" meta, condition + heart footer),
 * built only from the site's theme palette so it follows light/dark mode.
 */
const topCardStyles = {
  card: (theme: Theme) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    p: 1,
    borderRadius: 3.5,
    border: `1px solid ${theme.palette.mode === 'light' ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.6)}`,
    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.07 : 0.1),
    color: 'text.primary',
    textAlign: 'left',
    transition: theme.transitions.create(['box-shadow', 'border-color'], { duration: theme.transitions.duration.short }),
    '@media (hover: hover)': {
      '&:hover': {
        borderColor: 'primary.main',
        boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.18 : 0.12)}`
      }
    },
    '&:focus-within': {
      borderColor: 'primary.main'
    }
  }),
  media: {
    position: 'relative',
    width: '100%',
    aspectRatio: '6 / 5',
    flexShrink: 0,
    borderRadius: 2.5,
    overflow: 'hidden',
    bgcolor: 'action.hover'
  },
  image: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
    userSelect: 'none'
  },
  imageFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'text.disabled'
  },
  badge: (theme: Theme) => ({
    position: 'absolute',
    top: 6,
    right: 6,
    px: 0.75,
    py: 0.25,
    borderRadius: 1.5,
    bgcolor: 'primary.main',
    color: theme.palette.getContrastText(theme.palette.primary.main),
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: 0.4,
    pointerEvents: 'none'
  }),
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minWidth: 0,
    pt: 1,
    px: 0.25
  },
  title: (theme: Theme) => ({
    // Stretched link: the whole card is clickable while the heart stays a separate control
    position: 'static',
    m: 0,
    color: 'text.primary',
    textDecoration: 'none',
    fontSize: { xs: 13, sm: 14 },
    fontWeight: 500,
    // Whole-pixel line heights keep the rail's height integral, so content below it stays on the pixel grid
    lineHeight: { xs: '18px', sm: '20px' },
    minHeight: { xs: '36px', sm: '40px' }, // always reserve two lines so prices line up across cards
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    outline: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: '-1px', // cover the card including its border
      zIndex: 0,
      borderRadius: '14px'
    },
    '&:focus-visible::after': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    }
  }),
  price: {
    mt: 0.75,
    color: 'primary.main',
    fontSize: { xs: 15, sm: 16 },
    fontWeight: 700,
    lineHeight: { xs: '20px', sm: '22px' },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  meta: (theme: Theme) => ({
    mt: 0.25,
    color: metaColor(theme),
    fontSize: 12,
    lineHeight: '17px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }),
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 0.5,
    mt: 'auto',
    pt: 0.5,
    minHeight: 36
  },
  status: (theme: Theme) => ({
    flex: 1,
    minWidth: 0,
    color: metaColor(theme),
    fontSize: 12,
    lineHeight: '17px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }),
  favoriteButton: (theme: Theme) => ({
    position: 'relative',
    zIndex: 1, // above the stretched title link
    flexShrink: 0,
    width: 36,
    height: 36,
    mr: -0.75,
    mb: -0.5,
    color: 'error.main',
    transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
    '@media (hover: hover)': {
      '&:hover': {
        transform: 'scale(1.1)',
        backgroundColor: alpha(theme.palette.error.main, 0.08)
      }
    },
    '&:active': {
      transform: 'scale(0.9)'
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 1
    },
    '&.Mui-disabled': {
      color: 'error.main',
      opacity: 0.5
    }
  }),
  favoriteIcon: {
    fontSize: 22
  }
};

const styles = {
  cardContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    mx: { xs: 0, sm: 0 },
    py: 1,
    width: '100%',
    maxWidth: '100%'
  },
  addressStyle: {
    m: 0,
    color: 'text.secondary',
    fontWeight: 500,
    letterSpacing: -0.3,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.3
  },
  priceStyle: {
    m: 0,
    fontWeight: 700,
    fontStyle: 'normal',
    color: 'primary.main',
    letterSpacing: -0.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.3
  },
  cardStyle: {
    width: '100%',
    maxWidth: '100%',
    height: '100%',
    minHeight: { xs: 248, sm: 300, md: 330 },
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    borderRadius: 2,
    boxShadow: 1,
    overflow: 'hidden'
  },
  imageStyle: {
    width: '100%',
    aspectRatio: '1',
    flexShrink: 0,
    objectFit: 'cover',
    cursor: 'pointer'
  },
  skeletonStyle: {
    width: '100%',
    aspectRatio: '1',
    flexShrink: 0,
    borderRadius: 0
  },
  contentStyle: {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    textAlign: 'left',
    p: 1,
    pt: 1,
    '&:last-child': {
      pb: 1
    }
  },
  /** Always reserve two title lines so the footer row aligns across cards */
  titleBlockStyle: {
    flexShrink: 0,
    minHeight: { xs: 27, sm: 36 },
    mb: 0.5
  },
  titleStyle: {
    m: 0,
    lineHeight: 1.5,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    // long words / URLs wrap inside the 2-line clamp instead of running past the card
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    textAlign: 'left',
    letterSpacing: 0.1,
    color: 'text.primary',
    fontWeight: 500,
    cursor: 'pointer'
  },
  metaBlockStyle: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.25
  },
  itemStatusStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 0.5,
    marginTop: 'auto',
    flexShrink: 0,
    minHeight: 32,
    pt: 0.5
  },
  statusTextStyle: {
    m: 0,
    flex: 1,
    minWidth: 0,
    fontWeight: 500,
    color: 'text.secondary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.3
  },
  favoIconStyle: {
    textAlign: 'left',
    boxShadow: 0,
    '&:hover': {
      bgcolor: 'transparent'
    }
  },
  favoriteButton: {
    flexShrink: 0,
    width: 32,
    height: 32,
    p: 0.5,
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.1)',
      backgroundColor: 'transparent'
    },
    '&:active': {
      transform: 'scale(0.9)'
    }
  }
};
